import { Observable, Subject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Inject, Injectable, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { DateAdapter, ErrorStateMatcher, MAT_DATE_FORMATS, MatDateFormats, NativeDateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { platform } from '../../platform/platform';
import { ApplicationError } from '../_errors/application-error';
import { InspectionReport, InspectionReportInputGroup } from '../_objects/inspection-report';
import { EditableTextList, ReportTemplate } from '../_offline/models/report-preview/report-template';
import { UtilsService } from '../_services/_utils.service';
import { CommonSnackBarService } from '../_services/common-snack-bar.service';
import { InspectionReportService } from '../_services/http-api/inspection-report.service';
import { ReportTemplateService } from '../_services/http-api/report-template.service';
import { LangUpdateService } from '../_services/lang-update.service';
import { NetworkStatusService } from '../_services/network-status.service';
import { SpinnerService } from '../_services/spinner.service';
import { DATE_PICKER_VALIDATION, DOCUMENT_FILETYPE, FILE_TYPE_ITEMS, FILETYPEITEMS, INSPECTION_REPORT_STATUS_CODE, PLATFORM } from '../app.constants';
import { CommonErrorMessageDialogComponent } from '../common-dialog/common-error-message-dialog.component';
import { CommonSaveConfirmDialogComponent } from '../common-dialog/common-save-confirm-dialog.component';
import { ReportCopyConfirmDialogComponent } from '../inspection-report/inspection-report-edit-text-copy-confirm-dialog.component';
import { ReportEditableTextImportConfirmDialogComponent } from '../inspection-report/inspection-report-edit-text-import-confirm-dialog.component';
import { multiSelectEditableTextsSeparator } from '../app.constants';
import { FileManagerComponent, FileManagerConfig } from '../common-dialog/file-manager/file-manager.component';
import { OperationMode, SelectionMode } from '../common-dialog/file-manager/types';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl<string> | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

/**
 * 日付表示をyyyy/MM/ddにするために、
 * NativeDateAdapterのformat()をオーバーライドしている。
 */
@Injectable()
export class AppDateAdapter extends NativeDateAdapter {

  format(date: Date, displayFormat: string): string {
    if (displayFormat === 'dateInput') {
      // 'dateInput' は yyyy/MM/dd へフォーマットする。
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return year + '/' + this._to2digit(month) + '/' + this._to2digit(day);
    } else {
      // 'dateInput' 以外はNativeDateAdapterの振る舞いとする。
      return super.format(date, displayFormat);
    }
  }

  private _to2digit(n: number) {
    return ('00' + n).slice(-2);
  }
}

/**
 * dateInput の振る舞いだけを変更したいので、
 * dateInput 以外は、MAT_NATIVE_DATE_FORMATS のままにしておく。
 */
export const APP_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: null,
  },
  display: {
    dateInput: 'dateInput',
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

@Component({
    selector: 'app-dialog-report-editabletexts',
    styleUrls: ['./inspection-report-edit-text-dialog.component.scss'],
    templateUrl: './inspection-report-edit-text-dialog.component.html',
    providers: [
        { provide: DateAdapter, useClass: AppDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS }
    ],
    standalone: false
})
export class ReportEditableTextsEditDialogComponent implements OnInit, OnDestroy {

  reportId: string;
  reportTemplateId: string;
  inspectionReport: InspectionReport;
  editableTextsList: EditableTextList[] = [];
  reportTemplate: ReportTemplate;
  browserName: string;
  importFiles: File;

  // groupの実装
  inputGroupLists = {};
  inputGroups: InspectionReportInputGroup[] = [];
  inputGroupName = null;

  // editable texts
  reportEditableTexts: {} = {};
  preReportEditableTexts: {} = {};
  tmpCopyReportEditableTexts: {} = {};
  objectEditableTexts: string[] = [];
  radioLists = {};
  isEditableItems = false;
  isEditableTextsList = false;
  isValueChange = false;
  isProcess = false;
  isReload = false;

  // import file type用
  fileTypeControl: FormControl<string> = new FormControl<string>('');
  fileTypeItems: FILETYPEITEMS[];

  minDate = new Date(DATE_PICKER_VALIDATION.MIN_YEAR, DATE_PICKER_VALIDATION.MIN_MONTH - 1, DATE_PICKER_VALIDATION.MIN_DAY);
  maxDate = new Date(DATE_PICKER_VALIDATION.MAX_YEAR, DATE_PICKER_VALIDATION.MAX_MONTH - 1, DATE_PICKER_VALIDATION.MAX_DAY);

  matcher = new MyErrorStateMatcher();
  private onDestroy$ = new Subject();

  @Output() reload = new EventEmitter<boolean>();
  @ViewChild('edittextcontent') editTextContent;

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isWebAndDesktopOnline() {
    return !this.isDesktop
      || (this.isDesktop && this.networkStatusService.isOnline);
  }

  public get useApplicationFileManager() {
    return this.networkStatusService.useApplicationFileManager();
  }

  constructor(
    private inspectionReportService: InspectionReportService,
    private reportTemplateService: ReportTemplateService,
    public dialogRef: MatDialogRef<ReportEditableTextsEditDialogComponent>,
    private snackBar: CommonSnackBarService,
    private router: Router,
    private dialog: MatDialog,
    private utilsService: UtilsService,
    private oscSpinnerService: SpinnerService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private networkStatusService: NetworkStatusService) {
    this.langService.prepare(translate, this.onDestroy$);
    if (!data || data.reportId <= 0) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.MsgRptIDNotFound'));
    }
    this.reportId = data.reportId;
    this.reportTemplateId = data.reportTemplateId;
    this.browserName = data.browserName;
    this.isReload = data.isReload;
    this.inspectionReport = data.inspectionReport;
    this.editableTextsList = data.editableTextsList;
    this.fileTypeItems = FILE_TYPE_ITEMS;
    if (!this.fileTypeControl.value) {
      this.fileTypeControl.setValue(this.fileTypeItems[0].value);
    }
  }

  ngOnInit() {
    this.reportEditableTexts = this.convertEditableTexts(this.inspectionReport.editableTexts);
    if (Object.keys(this.inspectionReport.editableTexts).length !== 0) {
      this.preReportEditableTexts = JSON.parse(JSON.stringify(this.convertEditableTexts(this.inspectionReport.editableTexts)));
      this.tmpCopyReportEditableTexts = JSON.parse(JSON.stringify(this.convertEditableTexts(this.inspectionReport.editableTexts)));
    }
    if (this.editableTextsList !== null || !this.editableTextsList) {
      this.isEditableTextsList = true;
      if (!this.reportEditableTexts) {
        this.objectEditableTexts = this.getEditableTextsOfMaster();
      } else {
        this.objectEditableTexts = this.mergeEditableTextsItems();
      }
    } else {
      this.isEditableTextsList = false;
    }

    this.inputGroupLists = this.getInputGroupLists(this.editableTextsList);

    for (const key of Object.keys(this.inputGroupLists)) {
      this.inputGroups.push({
        name: key,
        isSelected: this.inspectionReport.excludedInputGroups.indexOf(key) === -1
      });
    }
    this.inputGroups = this.inputGroups.sort((a, b) => a.name.localeCompare(b.name));
    this.radioLists = this.getRadioGroupLabel(this.editableTextsList);
    this.isEditableItems = true;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  getInputGroupLists(editableTextsList: EditableTextList[]): {} {
    const tmpLists = {};
    let flg = false;
    let tmpInputGroup = null;
    if (editableTextsList === null || editableTextsList === undefined) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.MsgEditItemNotFound'));
    }
    editableTextsList.forEach(item => {
      if (item.inputGroup === null || item.inputGroup === undefined) {
        item.inputGroup = this.translate.instant('inspectionReportEditTextDialog.NoGroup');
      }
      if (!(item.inputGroup in tmpLists)) {
        tmpLists[item.inputGroup] = [];
        if (!flg) {
          tmpInputGroup = item.inputGroup;
          flg = true;
        }
      }
      tmpLists[item.inputGroup].push(item);
    });

    // 初期表示するグループ名を取得
    this.inputGroupName = tmpInputGroup;

    return tmpLists;
  }

  getRadioGroupLabel(editableTextsList: EditableTextList[]): {} {
    const tmpLists = {};
    if (editableTextsList === null || editableTextsList === undefined) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.MsgEditItemNotFound'));
    }
    editableTextsList.forEach(item => {
      if (item.inputType === 'radio') {
        if (!(item.label in tmpLists)) {
          tmpLists[item.label] = [];
          tmpLists[item.label][item.radioLabel] = true;
        }
      }
    });
    return tmpLists;
  }

  isRequiredNotInput(inputGroup: string, reportEditableTexts: {}): boolean {
    let flg = false;
    if (inputGroup === null || inputGroup === undefined || reportEditableTexts === undefined) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.ErrInvalidIsReq'));
    }
    this.editableTextsList.forEach(item => {
      if (item.inputGroup === inputGroup && item.validation.required) {
        if (reportEditableTexts[item.editableId] !== undefined) {
          if (reportEditableTexts[item.editableId] === ''
            || reportEditableTexts[item.editableId] === null || reportEditableTexts[item.editableId].length <= 0) {
            flg = true;
          }
        }
      }
    });
    return flg;
  }

  getTabItem(key: string): string {
    if (key === null || key === undefined) {
      key = '';
    }
    if (this.isValueChange) {
      this.submit(this.reportEditableTexts, this.inputGroups, false);
    }
    this.editTextContent.nativeElement.scrollIntoView();
    return this.inputGroupName = key;
  }
  checkInputTypeDate(reportEditableTexts: {}, editableId: string) {
    if (reportEditableTexts === null || reportEditableTexts === undefined ||
      editableId === null || editableId === undefined) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.ErrInvalidInspectionRpt'));
    }
    for (const editableText of this.editableTextsList) {
      if (editableText.inputType === 'date') {
        if (reportEditableTexts[editableId]) {
          if (Object.prototype.toString.call(reportEditableTexts[editableId]).slice(8, -1) === 'Date') {
            const theDate = reportEditableTexts[editableId];
            const year = theDate.getFullYear().toString();
            const month = ('0' + (theDate.getMonth() + 1)).slice(-2);
            const date = ('0' + theDate.getDate()).slice(-2);
            this.reportEditableTexts[editableId] = year + '-' + month + '-' + date + 'T00:00:00';
          }
        }
      }
    }
  }

  isCheckChange(reportEditableTexts: {}, preReportEditableTexts: {}): boolean {
    if (!reportEditableTexts || !preReportEditableTexts) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.ErrInvalidInspectionRpt'));
    }
    const current = JSON.stringify(reportEditableTexts);
    const previous = JSON.stringify(preReportEditableTexts);

    return current !== previous
      || this.isSelectedExcludedInputGroupsSameAsReport();
  }

  isSelectedExcludedInputGroupsSameAsReport() : boolean {
    const selectedExcludedInputGroups = this.inputGroups.filter(group => !group.isSelected).map(group => group.name);
    return JSON.stringify(selectedExcludedInputGroups) !== JSON.stringify(this.inspectionReport.excludedInputGroups);
  }

  close() {
    this.reload.emit(this.isReload);
    if (this.isValueChange) {
      const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, {
        width: '550px',
        height: '300px',
        data: { isMove: false, isError: false }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.submit(this.reportEditableTexts, this.inputGroups, true);
        } else if (result !== '') {
          this.dialogRef.close();
        }
      });
    } else {
      this.dialogRef.close();
    }
  }
  // Template Master loading
  getEditableTextsOfMaster(): string[] {
    const tmpEditableTexts = this.objectEditableTexts;
    for (const master of this.editableTextsList) {
      tmpEditableTexts.push(master.editableId);
    }
    return tmpEditableTexts;
  }

  // Current texts loading
  getEditableTextsOfCurrent(): string[] {
    const tmpCurrentEditableTexts: string[] = [];
    for (const key of Object.keys(this.reportEditableTexts)) {
      tmpCurrentEditableTexts.push(key);
    }
    return tmpCurrentEditableTexts;
  }

  initInputTypeSelect(editableTextList: EditableTextList, editableId: string) {
    if (!editableTextList || editableTextList.inputType === null || editableTextList.inputType === undefined || editableId === null || editableId === undefined) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.ErrInvalidEditTextLst'));
    }
    if (!this.preReportEditableTexts[editableId] && editableTextList.inputType === 'select' && !editableTextList.validation.required
      && !this.inspectionReport.editableTexts[editableId]) {
      this.reportEditableTexts[editableId] = null;
      this.preReportEditableTexts[editableId] = null;
    }
  }
  // Master & current texts marge
  mergeEditableTextsItems(): string[] {
    const result = [];
    for (const editableText of this.editableTextsList) {
      const editableId = editableText.editableId;
      result.push(editableId);
      if (this.reportEditableTexts[editableId] === undefined) {
        if (editableText.text) {
          this.reportEditableTexts[editableId] = editableText.text;
          this.preReportEditableTexts[editableId] = editableText.text;
          this.tmpCopyReportEditableTexts[editableId] = editableText.text;
        } else {
          this.reportEditableTexts[editableId] = '';
          this.preReportEditableTexts[editableId] = '';
          this.tmpCopyReportEditableTexts[editableId] = '';
          this.initInputTypeSelect(editableText, editableId);
        }
      } else {
        this.initInputTypeSelect(editableText, editableId);
      }
    }
    return result;
  }

  // load inspectionReport
  getInspectionReport(reportId: string): Observable<InspectionReport> {
    if (!reportId) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.ErrNullRptID'));
    }
    return this.inspectionReportService.getInspectionReport(reportId);
  }

  convertEditableTexts(objectEditableTexts: {}): {} {
    // null は空文字に変更
    for (const key of Object.keys(objectEditableTexts)) {
      if (objectEditableTexts[key] === null) {
        objectEditableTexts[key] = '';
      }
    }

    // Date型のデータはUTC時刻のstring型に変更
    this.editableTextsList.forEach(item => {
      if (item.inputType === 'date') {
        if (objectEditableTexts[item.editableId]) {
          if (Object.prototype.toString.call(objectEditableTexts[item.editableId]).slice(8, -1) === 'Date') {
            const theDate = objectEditableTexts[item.editableId];
            const year = theDate.getFullYear().toString();
            const month = ('0' + (theDate.getMonth() + 1)).slice(-2);
            const date = ('0' + theDate.getDate()).slice(-2);
            objectEditableTexts[item.editableId] = year + '-' + month + '-' + date + 'T00:00:00';
          }
        }
        // 複数選択は配列とString型を変換
      } else if (item.inputType === 'multiSelect') {
        if (objectEditableTexts[item.editableId]) {
          if (typeof objectEditableTexts[item.editableId] === 'string') {
            objectEditableTexts[item.editableId] = (objectEditableTexts[item.editableId] as string).split(multiSelectEditableTextsSeparator);
          } else {
            objectEditableTexts[item.editableId] = (objectEditableTexts[item.editableId] as string[]).join(multiSelectEditableTextsSeparator);
          }
        }
      }
    });

    return objectEditableTexts;
  }

  submit(objectEditableTexts: {}, excludedInputGroups: InspectionReportInputGroup[], isClose: boolean): void {
    if (objectEditableTexts === null || objectEditableTexts === undefined) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.ErrNullEditText'));
    }

    if (this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.COMPLETED) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.ErrRptStsCompleted'));
    }

    if (this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.EDITING
      || this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.FAILED) {
      this.oscSpinnerService.show(true);
      this.isProcess = true;
      objectEditableTexts = JSON.parse(JSON.stringify(this.convertEditableTexts(this.inspectionReport.editableTexts)));

      const tmpInspectionReport: InspectionReport = this.inspectionReport;
      tmpInspectionReport.editableTexts = objectEditableTexts;
      tmpInspectionReport.excludedInputGroups = excludedInputGroups.filter(group => !group.isSelected).map(group => group.name);

      const inspectionReportUpdateEditableTexts
        = this.inspectionReportService.convertInspectionReport2InspectionReportUpdateTexts(tmpInspectionReport);
      this.inspectionReportService.patchInspectionReportTexts(inspectionReportUpdateEditableTexts)
        .subscribe(iru => {
          this.data.inspectionReport.editableTexts = iru.editableTexts;
          this.data.inspectionReport.excludedInputGroups = iru.excludedInputGroups;
        },
        (error: HttpErrorResponse) => {
          this.oscSpinnerService.hide(true);
          this.isValueChange = true;
          this.isProcess = false;
          if (!error.status || error.status === 403 || error.status === 0) {
            this.openErrorMessageDialog(error.status);
          } else {
            this.snackBar.open(this.translate.instant('inspectionReportEditTextDialog.FailSave'));
          }
          this.inspectionReport.editableTexts = this.convertEditableTexts(objectEditableTexts);
          this.reportEditableTexts = this.inspectionReport.editableTexts;
          console.log(error);
        },
        () => {
          this.snackBar.open(this.translate.instant('inspectionReportEditTextDialog.SuccessSave'));
          this.inspectionReport.editableTexts = this.convertEditableTexts(objectEditableTexts);
          this.inspectionReport.excludedInputGroups = this.data.inspectionReport.excludedInputGroups;
          this.preReportEditableTexts = JSON.parse(JSON.stringify(this.inspectionReport.editableTexts));
          this.reportEditableTexts = this.inspectionReport.editableTexts;
          for (const editableText of this.editableTextsList) {
            const editableId = editableText.editableId;
            this.initInputTypeSelect(editableText, editableId);
          }
          this.oscSpinnerService.hide(true);
          this.isValueChange = false;
          this.isProcess = false;
          this.isReload = true;
          if (isClose) { this.dialogRef.close(true); }
        });
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  copyValue(editableId: string, value: string) {
    // アンスコ2連続を含む場合のみコピー対象とする
    if (editableId.match(/__/)) {
      if (this.reportEditableTexts[editableId] !== this.tmpCopyReportEditableTexts[editableId]) {
        const dialogRef = this.dialog.open(ReportCopyConfirmDialogComponent, {
          width: '550px',
          height: '300px'
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result === 'yes') {
            // アンスコ2連続より前の文字列を抽出
            const editableMasterId = editableId.substring(0, editableId.indexOf('__'));
            for (const key of Object.keys(this.reportEditableTexts)) {
              if (key.match(/__/) && key.substring(0, key.indexOf('__')) === editableMasterId && key !== editableId) {
                this.reportEditableTexts[key] = value;
                this.checkInputTypeDate(this.reportEditableTexts, key);
              }
            }
            this.isValueChange = this.isCheckChange(this.reportEditableTexts, this.preReportEditableTexts);
          }
        });
      }
    }
    this.checkInputTypeDate(this.reportEditableTexts, editableId);
    this.isValueChange = this.isCheckChange(this.reportEditableTexts, this.preReportEditableTexts);
    this.tmpCopyReportEditableTexts = JSON.parse(JSON.stringify(this.reportEditableTexts));
  }

  onCheckBoxChange() {
    this.isValueChange = this.isCheckChange(this.reportEditableTexts, this.preReportEditableTexts);
  }

  isSafari(browserName): boolean {
    return this.utilsService.isSafari(browserName);
  }

  checkFileType(fileName: string, format: string): boolean {
    const type = fileName.split('.');
    if (format === 'xlsx1' && type[type.length - 1].toLowerCase() === 'xlsx') {
      return true;
    } else {
      return false;
    }
  }

  isDateSlash(dateString: string): boolean {
    const date = dateString.split('/');

    if (date[0].length > 2 || date[1].length > 2 || date[2].length !== 4) {
      return false;
    }

    const month = ('0' + date[0]).slice(-2);
    const day = ('0' + date[1]).slice(-2);
    const year = ('000' + date[2]).slice(-4);

    // month/day/year
    if (date.length !== 3 || month.match(/0[1-9]|1[0-2]/) === null || month.match(/0[1-9]|1[0-2]/) === undefined
      || day.match(/0[1-9]|[12][0-9]|3[01]/) === null || day.match(/0[1-9]|[12][0-9]|3[01]/) === undefined
      || year.match(/\d{4}/) === null || year.match(/\d{4}/) === undefined) {
      return false;
    } else {
      return true;
    }
  }

  // yyyy-mm-dd
  isDateHyphen(dateString: string): boolean {
    const date = dateString.split('-');

    if (date[0].length !== 4 || date[1].length > 2 || date[2].length > 2) {
      return false;
    }
    const year = ('000' + date[0]).slice(-4);
    const month = ('0' + date[1]).slice(-2);
    const day = ('0' + date[2]).slice(-2);

    // year/month/day
    if (date.length !== 3 || month.match(/0[1-9]|1[0-2]/) === null || month.match(/0[1-9]|1[0-2]/) === undefined
      || day.match(/0[1-9]|[12][0-9]|3[01]/) === null || day.match(/0[1-9]|[12][0-9]|3[01]/) === undefined
      || year.match(/\d{4}/) === null || year.match(/\d{4}/) === undefined) {
      return false;
    } else {
      return true;
    }
  }

  changeDate(editableId: string, code: string) {
    const dateString = this.inspectionReport.editableTexts[editableId];
    let month;
    let day;
    let year;
    if (code === '/') {
      month = (String(dateString)).split(code)[0];
      day = (String(dateString)).split(code)[1];
      year = (String(dateString)).split(code)[2];
    } else if (code === '-') {
      year = (String(dateString)).split(code)[0];
      month = (String(dateString)).split(code)[1];
      day = (String(dateString)).split(code)[2];
    }
    this.inspectionReport.editableTexts[editableId]
      = ('000' + year).slice(-4) + '-'
      + ('0' + month).slice(-2) + '-'
      + ('0' + day).slice(-2) + 'T00:00:00';
  }

  // 登録された日付がdate formatだったら変換する処理
  changeDateFormat(): void {
    for (const editableText of this.editableTextsList) {
      if (editableText.inputType === 'date') {
        if (this.inspectionReport.editableTexts[editableText.editableId]) {
          if (Object.prototype.toString.call(this.inspectionReport.editableTexts[editableText.editableId]).slice(8, -1) === 'Date') {
            const theDate = this.inspectionReport.editableTexts[editableText.editableId];
            const year = theDate.getFullYear().toString();
            const month = ('0' + (theDate.getMonth() + 1)).slice(-2);
            const date = ('0' + theDate.getDate()).slice(-2);
            this.inspectionReport.editableTexts[editableText.editableId] = year + '-' + month + '-' + date + 'T00:00:00';
          } else if (this.isDateSlash(this.inspectionReport.editableTexts[editableText.editableId])) {
            this.changeDate(editableText.editableId, '/');
          } else if (this.isDateHyphen(this.inspectionReport.editableTexts[editableText.editableId])) {
            this.changeDate(editableText.editableId, '-');
          }
        }
      }
    }
  }

  importInspectionReportEditabelTexts(files: File[]): void {
    if (!files || files.length !== 1) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.ErrInvalidFile'));
    }

    if (this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.COMPLETED) {
      throw new ApplicationError(this.translate.instant('inspectionReportEditTextDialog.MsgRptCompleted'));
    }

    const file = files[0];
    this.importFiles = null;

    const fileType = this.fileTypeControl.value;

    this.openImportConfirmDialog(file.name).subscribe(val => {
      if (val.result) {
        if (fileType && this.checkFileType(file.name, fileType)) {
          this.oscSpinnerService.show(true);
          this.isProcess = true;
          this.inspectionReportService.importInspectionReportTexts(this.inspectionReport, file, fileType)
            .subscribe(inspectionReport => {
              this.inspectionReport.editableTexts = inspectionReport.editableTexts;
            },
            (error: HttpErrorResponse) => {
              this.oscSpinnerService.hide(true);
              this.isProcess = false;
              if (error.status === 400 || error.status === 500) {
                this.openErrorMessageDialog(error.status);
              } else {
                console.log(error);
                this.snackBar.open(this.translate.instant('inspectionReportEditTextDialog.FailImport'));
              }
            },
            () => {
              this.changeDateFormat();
              this.data.inspectionReport.editableTexts = this.inspectionReport.editableTexts;
              this.reportEditableTexts = this.inspectionReport.editableTexts;
              this.mergeEditableTextsItems();
              this.oscSpinnerService.hide(true);
              this.isReload = true;
              this.isProcess = false;
              this.snackBar.open(this.translate.instant('inspectionReportEditTextDialog.SuccessImport'));
            });
        } else {
          this.snackBar.open(this.translate.instant('inspectionReportEditTextDialog.ErrInvalidFileType'));
        }
      }
    });
  }

  openImportConfirmDialog(filename: string): Observable<{ result: boolean }> {
    const dialogRef = this.dialog.open(ReportEditableTextImportConfirmDialogComponent, {
      width: '550px',
      height: '300px',
      data: { filename: filename }
    });
    return dialogRef.afterClosed();
  }

  openErrorMessageDialog(status: number): void {
    if (status === 500) {
      this.dialog.open(CommonErrorMessageDialogComponent, {
        width: '450px',
        height: '374px',
        data: {
          firstMessage: this.translate.instant('inspectionReportEditTextDialog.FailImport'),
          secondMessage: this.translate.instant('inspectionReportEditTextDialog.MsgCheckFileOrContact'),
        }
      });
    } else if (status === 400) {
      this.dialog.open(CommonErrorMessageDialogComponent, {
        width: '500px',
        height: '374px',
        data: {
          firstMessage: this.translate.instant('inspectionReportEditTextDialog.FailImport'),
          secondMessage: this.translate.instant('inspectionReportEditTextDialog.MsgCheckFileTextOrContact'),
        }
      });
    } else if (!status || status === 403 || status === 0) {
      this.dialog.open(CommonErrorMessageDialogComponent, {
        width: '580px',
        height: '374px',
        data: {
          firstMessage: this.translate.instant('inspectionReportEditTextDialog.FailSave'),
          secondMessage: this.translate.instant('inspectionReportEditTextDialog.MsgCheckRptTextOrContact'),
        }
      });
    }
  }

  checkMaxLength(item: string | Array<string>, maxLength: number): boolean {
    var SumValueString = "";
    if (item === null || item === "") return false;
    (item as Array<string>).forEach((value: string) => {
      SumValueString += value;
    });
    return SumValueString.length > maxLength;
  }

  showFileManager() {
    const supportedExtensions = [DOCUMENT_FILETYPE.EXTENSION_XLSX];

    const fileManagerConfig: MatDialogConfig<FileManagerConfig> = {
      disableClose: true,
      ...FileManagerComponent.DEFAULT_WINDOW_SIZE,
      data: {
        operationMode: OperationMode.Import,
        selectionMode: SelectionMode.SingleFile,
        extensions: supportedExtensions,
        handleImportedSelection: this.importInspectionReportEditabelTexts.bind(this),
      },
    };

    this.dialog.open(FileManagerComponent, fileManagerConfig);
  }
}
